import React, { useEffect } from "react";
import { MantineProvider } from "@mantine/core";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "./store";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Login from "./pages/Login";
import PrivateRoute from "./components/PrivateRoute";
import { ShipperSelector } from "./pages/ShipperSelector";
import Orders from "./pages/Orders";
import Integration from "./pages/integrations/Integration";
import IntegrationCallback from "./pages/integrations/IntegrationCallback";
import Customers from "./pages/Customers";
import Warehouses from "./pages/Warehouses";
import IntegrationOptions from "pages/integrations/IntegrationOptions";
import Trips from "./pages/Trips";
import Vehicles from "pages/Vehicles";
import Couriers from "pages/Couriers";
import UserManagement from "pages/UserManagement";

const AppContent = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect admin/support users to shipper selector if no shipper is selected
    if (user && ["ADMIN", "SUPPORT"].includes(user.type)) {
      const selectedShipper = localStorage.getItem("owlara_selected_shipper");
      if (!selectedShipper && location.pathname !== "/select-shipper") {
        navigate("/select-shipper");
      }
    }
  }, [user, location.pathname, navigate]);

  return (
    <Layout>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
        <Route
          path="/select-shipper"
          element={
            !user ? (
              <Navigate to="/login" />
            ) : !["ADMIN", "SUPPORT"].includes(user.type) ? (
              <Navigate to="/" />
            ) : (
              <ShipperSelector />
            )
          }
        />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Home />
            </PrivateRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <PrivateRoute>
              <Orders />
            </PrivateRoute>
          }
        />
        <Route
          path="/trips"
          element={
            <PrivateRoute>
              <Trips />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/integrations"
          element={
            <PrivateRoute>
              <Integration />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/integrations/:integrationName/callback"
          element={<IntegrationCallback />}
        />
        <Route
          path="/settings/integrations/:integrationName/options"
          element={<IntegrationOptions />}
        />
        <Route
          path="/settings/user-management"
          element={
            <PrivateRoute>
              <UserManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/vehicles"
          element={
            <PrivateRoute>
              <Vehicles />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/couriers"
          element={
            <PrivateRoute>
              <Couriers />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/customers"
          element={
            <PrivateRoute>
              <Customers />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/warehouses"
          element={
            <PrivateRoute>
              <Warehouses />
            </PrivateRoute>
          }
        />
      </Routes>
    </Layout>
  );
};

const App = () => {
  return (
    <MantineProvider defaultColorScheme="dark">
      <Router>
        <AppContent />
      </Router>
    </MantineProvider>
  );
};

export default App;
